import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { ApiError, getMe, login, logout, onUnauthorized } from "@/api/client";
import type { Me, StaffMe, Who } from "@/api/client";

export type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  /** The server could not be asked; not the same as signed out. */
  | { status: "unreachable" }
  | { status: "signed-in"; me: Me }
  /** Darkstar staff, in the platform console (ADR-0015). */
  | { status: "staff"; staff: StaffMe };

function signedIn(who: Who): AuthState {
  return who.kind === "staff" ? { status: "staff", staff: who } : { status: "signed-in", me: who };
}

interface Auth {
  state: AuthState;
  /** Resolves to who signed in, so the caller knows where they belong. */
  signIn: (email: string, password: string) => Promise<Who>;
  signOut: () => Promise<void>;
  retry: () => void;
  /** Signed in by another route, such as accepting an invite or a staff link, which
   * answered `me`. */
  adopt: (who: Who) => void;
}

/** Exported for tests, which give a signed-in user instead of asking for one. */
export const AuthContext = createContext<Auth | null>(null);

/**
 * Who is signed in, asked of `/api/auth/me` once on load (ADR-0012). Any request that
 * later answers 401 marks the user signed out, and the guard sends them to sign in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((who) => {
        if (!cancelled) setState(signedIn(who));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const out = err instanceof ApiError && err.status === 401;
        setState({ status: out ? "signed-out" : "unreachable" });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    onUnauthorized(() => setState({ status: "signed-out" }));
    return () => onUnauthorized(null);
  }, []);

  // Darkstar staff's view of a workspace ends on its own (ADR-0018): ask again when it
  // does, and the guard sends them back to the console.
  const viewEnds = state.status === "signed-in" ? state.me.staff_view?.until : undefined;
  useEffect(() => {
    if (!viewEnds) return;
    const timer = window.setTimeout(
      () => {
        getMe()
          .then((who) => setState(signedIn(who)))
          .catch(() => setState({ status: "signed-out" }));
      },
      Math.max(0, Date.parse(viewEnds) - Date.now()) + 500,
    );
    return () => window.clearTimeout(timer);
  }, [viewEnds]);

  const signIn = useCallback(async (email: string, password: string) => {
    const who = await login(email, password);
    setState(signedIn(who));
    return who;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logout();
    } finally {
      setState({ status: "signed-out" });
    }
  }, []);

  const retry = useCallback(() => {
    setState({ status: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  const adopt = useCallback((who: Who) => setState(signedIn(who)), []);

  const value = useMemo(
    () => ({ state, signIn, signOut, retry, adopt }),
    [state, signIn, signOut, retry, adopt],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error("useAuth needs an <AuthProvider> above it");
  }
  return auth;
}

/** The signed-in user. Only for pages behind `RequireAuth`. */
export function useMe(): Me {
  const { state } = useAuth();
  if (state.status !== "signed-in") {
    throw new Error("useMe is only for pages behind <RequireAuth>");
  }
  return state.me;
}

/** The signed-in staff member. Only for pages behind `<RequireAuth staff />`. */
export function useStaff(): StaffMe {
  const { state } = useAuth();
  if (state.status !== "staff") {
    throw new Error("useStaff is only for pages behind <RequireAuth staff />");
  }
  return state.staff;
}

/** A permission name from `/api/auth/me`, such as `documents:upload` (ADR-0012). */
export type Permission =
  | "documents:read"
  | "documents:upload"
  | "review:write"
  | "documents:delete"
  | "llm_policy:change"
  | "knowledge:manage"
  | "members:manage"
  | "org:manage"
  | "owners:manage"
  | "org:licence:read"
  | "audit:read";

/** What a read-only organization (its licence ended, or Darkstar staff viewing it) may
 * still do. */
const READS: readonly Permission[] = ["documents:read", "org:licence:read", "audit:read"];

/**
 * Whether the signed-in user's role may do this. No one signed in may do nothing: an
 * action is hidden unless the server said it is allowed. While the organization is
 * read-only only reading is. The API checks it again.
 */
export function useCan(permission: Permission): boolean {
  const auth = useContext(AuthContext);
  if (auth?.state.status !== "signed-in") return false;
  const { me } = auth.state;
  return me.permissions.includes(permission) && (!me.read_only || READS.includes(permission));
}
